/**
 * Старт секретного чата (изменение v1.13, C-38 / P-04 «Секретный чат»).
 *
 * Порядок: убедиться, что ключи этого устройства опубликованы; `POST /secret-chats`;
 * по набору ключей устройства собеседника открыть сессию X3DH; положить диалог в
 * реплику — она единственный источник экрана чата — и перейти в него.
 */
import { useCallback, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import { conversationsRepo } from '@shared/db'
import { hasSecretSession, publishDeviceKeys, startSecretSession } from '@shared/secret'

export type StartSecretChatFailure =
  | 'unsupported'
  | 'no_device'
  | 'keys_missing'
  | 'user_unavailable'
  | 'peer_unsupported'
  | 'rate_limited'
  | 'network'

export class StartSecretChatError extends Error {
  constructor(public readonly reason: StartSecretChatFailure) {
    super(reason)
    this.name = 'StartSecretChatError'
  }
}

export function startSecretChatMessage(reason: StartSecretChatFailure): string {
  switch (reason) {
    case 'unsupported':
      return 'Этот браузер не поддерживает секретные чаты. Обновите его или откройте приложение на телефоне.'
    case 'peer_unsupported':
      return 'У собеседника нет устройства с поддержкой секретных чатов.'
    case 'user_unavailable':
      return 'Пользователь недоступен'
    case 'rate_limited':
      return 'Слишком много новых чатов — попробуйте позже'
    case 'keys_missing':
      return 'Не удалось опубликовать ключи устройства'
    case 'no_device':
      return 'Сессия устройства не установлена — войдите заново'
    default:
      return 'Не удалось начать секретный чат'
  }
}

export async function startSecretChat(userId: string, deviceId: string | null | undefined): Promise<string> {
  if (!deviceId) throw new StartSecretChatError('no_device')
  let outcome
  try {
    outcome = await publishDeviceKeys(deviceId)
  } catch {
    throw new StartSecretChatError('keys_missing')
  }
  if (outcome === 'unsupported') throw new StartSecretChatError('unsupported')
  try {
    const started = unwrap(await api.POST('/secret-chats', { body: { user_id: userId } }))
    const conversation = started.conversation
    if (started.bundle) await startSecretSession(conversation.id, userId, started.bundle)
    else if (!(await hasSecretSession(conversation.id))) {
      // Сервер помнит чат, а локальной сессии нет (например, сброшена база): такой чат
      // читать нечем — закрываем его для обоих и начинаем заново.
      await api.DELETE('/conversations/{conversationId}', {
        params: { path: { conversationId: conversation.id } },
      })
      return startSecretChat(userId, deviceId)
    }
    await conversationsRepo.put(conversation)
    return conversation.id
  } catch (error) {
    if (error instanceof StartSecretChatError) throw error
    if (error instanceof ApiError) {
      if (error.code === 'secret_chat_unavailable') throw new StartSecretChatError('peer_unsupported')
      if (error.code === 'secret_keys_missing') throw new StartSecretChatError('keys_missing')
      if (error.code === 'user_unavailable') throw new StartSecretChatError('user_unavailable')
      if (error.code === 'rate_limited') throw new StartSecretChatError('rate_limited')
    }
    throw new StartSecretChatError('network')
  }
}

export function useStartSecretChat(deviceId: string | null | undefined): {
  start: (userId: string) => Promise<string>
  busy: boolean
} {
  const [busy, setBusy] = useState(false)
  const start = useCallback(
    async (userId: string): Promise<string> => {
      setBusy(true)
      try {
        return await startSecretChat(userId, deviceId)
      } finally {
        setBusy(false)
      }
    },
    [deviceId],
  )
  return { start, busy }
}
